import { ref } from "vue-demi";
const DEFAULT_CONFIG = {
  baseUrl: "",
  endpoints: {
    forms: "/api/forms",
    countries: "/api/countries",
    cities: "/api/cities"
  }
};
const PRELOADED_FIELDS = ["COUNTRY"];
export const FIELDS_WITH_DEPENDENT_FIELDS = {
  COUNTRY: "CITY"
};
const prepareSelectCountryFieldData = (loadedCountries) => {
  return loadedCountries.map((item) => ({
    label: item["name"],
    value: item["iso2"]
  }));
};
const prepareSelectCityFieldData = (loadedCities) => {
  return loadedCities.map((item) => ({
    label: item["name"],
    value: item["it4_id"]
  }));
};
const updateFieldData = (attrName, stepIndex, fieldName, preparedFieldData, formData) => {
  const data = { ...formData };
  const fieldToUpdateIndex = data.steps[stepIndex].fields.findIndex(
    (field) => field.name === fieldName
  );
  if (fieldToUpdateIndex !== -1) {
    data.steps[stepIndex].fields[fieldToUpdateIndex].attributes[attrName] = preparedFieldData;
    return data;
  }
  return data;
};
export const createFormsApi = (config = {}) => {
  const apiConfig = {
    ...DEFAULT_CONFIG,
    ...config,
    endpoints: {
      ...DEFAULT_CONFIG.endpoints,
      ...config.endpoints
    }
  };
  const countries = ref([]);
  const fetchCountries = async (apiConfig2) => {
    const params = new URLSearchParams({
      limit: "9999",
      it4: "1"
    });
    const response = await fetch(
      `${apiConfig2.baseUrl}${apiConfig2.endpoints.countries}?${params.toString()}`
    );
    if (!response.ok) {
      throw new Error(`Failed to fetch form countries: ${response.statusText}`);
    }
    return response.json().then((data) => {
      countries.value = data.data;
      return data.data;
    });
  };
  const fetchCities = async (apiConfig2, countryIso2) => {
    const countryId = countries.value.find(
      (country) => country.iso2 === countryIso2
    )?.id;
    const params = new URLSearchParams({
      country_id: countryId,
      limit: "9999",
      it4: "1"
    });
    const response = await fetch(
      `${apiConfig2.baseUrl}${apiConfig2.endpoints.cities}?${params.toString()}`
    );
    if (!response.ok) {
      throw new Error(`Failed to fetch form cities: ${response.statusText}`);
    }
    return response.json().then((data) => {
      return data.data;
    });
  };
  const FIELDS_WITH_PRELOADED_DATA = {
    COUNTRY: {
      getData: fetchCountries.bind(null, apiConfig),
      prepareData: prepareSelectCountryFieldData,
      updateData: updateFieldData.bind(null, "options")
    }
  };
  const FIELDS_WITH_DEPENDENT_DATA = {
    CITY: {
      getData: fetchCities.bind(null, apiConfig),
      prepareData: prepareSelectCityFieldData,
      updateData: updateFieldData.bind(null, "options")
    }
  };
  const preloadFieldsData = async (formData) => {
    let updatedFormData = { ...formData };
    for (const [stepIndex, step] of updatedFormData.steps.entries()) {
      for (const field of step.fields) {
        if (PRELOADED_FIELDS.includes(field.name)) {
          const { getData, prepareData, updateData } = FIELDS_WITH_PRELOADED_DATA[field.name];
          const preloadedFieldData = await getData();
          const preparedFieldData = prepareData(preloadedFieldData);
          updatedFormData = updateData(
            stepIndex,
            field.name,
            preparedFieldData,
            updatedFormData
          );
        }
      }
    }
    return updatedFormData;
  };
  return {
    async getFormSchema(formId, params = { lang: "en" }) {
      const query = new URLSearchParams({
        lang: params?.lang
      });
      const response = await fetch(
        `${apiConfig.baseUrl}${apiConfig.endpoints.forms}/${formId}?${query.toString()}`
      );
      if (!response.ok) {
        throw new Error(`Failed to fetch form schema: ${response.statusText}`);
      }
      return response.json().then(async (data) => {
        const formSchema = await preloadFieldsData(data.data);
        return formSchema;
      });
    },
    async getFormDataWithUpdatedDependentFieldData(stepIndex, dependentFieldName, dependencyFieldValue, formData) {
      const { getData, prepareData, updateData } = FIELDS_WITH_DEPENDENT_DATA[dependentFieldName];
      const newFieldData = await getData(dependencyFieldValue);
      const preparedFieldData = prepareData(newFieldData);
      return updateData(
        stepIndex,
        dependentFieldName,
        preparedFieldData,
        formData
      );
    },
    async submitForm(formId, values) {
      const { recaptcha, ...rest } = values;
      const headers = {
        "Content-Type": "application/json"
      };
      if (recaptcha) {
        headers["Captcha-Token"] = recaptcha;
      }
      const response = await fetch(
        `${apiConfig.baseUrl}${apiConfig.endpoints.forms}/${formId}`,
        {
          method: "POST",
          headers,
          body: JSON.stringify(rest)
        }
      );
      if (!response.ok) {
        let errorMessage = `Failed to submit form: ${response.status}`;
        try {
          const data = await response.json();
          if (data?.message) {
            errorMessage = data.message;
          }
        } catch {
          errorMessage = "Failed to submit form";
        }
        throw new Error(errorMessage);
      }
      return response.json();
    }
  };
};
export const formsApi = createFormsApi();
